import type { AssistantConfirm, Delivery } from '@secretario/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { DELIVERIES_KEY } from '../../lib/api/deliveries.js';
import { ApiError, describeError } from '../../lib/api/http.js';
import { markDraftHandled } from '../../lib/assistantDraft.js';
import { completedDelivery, deliveryInProgress, startDelivery } from '../../lib/deliveryJobs.js';
import { hapticSuccess, hapticTap, hapticWarn } from '../../lib/native.js';
import { openPaywall } from '../../lib/plan.js';
import './assistant-modal.css';

/**
 * Pedido de documento que o Secretário deixou pronto para conferir: o que vai
 * ser preparado, com que dados, e a decisão da pessoa (preparar, ajustar na
 * página do pedido ou deixar para depois). Nada é preparado antes do toque.
 */
export function ServiceRequestCard({
  activityId,
  confirm,
  onReady,
  onDismiss,
}: {
  activityId: string;
  confirm: AssistantConfirm;
  /** O documento ficou pronto (a pessoa pode abri-lo). */
  onReady?: (delivery: Delivery) => void;
  onDismiss: () => void;
}) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(() => deliveryInProgress(activityId));
  // Já preparado antes (a pessoa fechou e voltou): o cartão mostra "Pronto", sem pedir de novo.
  const [ready, setReady] = useState<{ id: string; title: string } | null>(() =>
    completedDelivery(activityId),
  );
  const [error, setError] = useState('');
  // Preparo demorado: depois de alguns segundos, a pessoa fica sabendo que é assim mesmo.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!busy) {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(timer);
  }, [busy]);

  async function prepare() {
    if (busy || ready) return;
    setBusy(true);
    setError('');
    hapticTap();
    try {
      const delivery = await startDelivery(activityId, confirm.request);
      void queryClient.invalidateQueries({ queryKey: DELIVERIES_KEY });
      hapticSuccess();
      setReady(delivery);
      onReady?.(delivery);
    } catch (cause) {
      hapticWarn();
      if (cause instanceof ApiError && cause.status === 402) {
        openPaywall('services', 'documento-conversa');
        setError('Documentos prontos entram no plano Completo.');
      } else {
        setError(describeError(cause, 'Não consegui preparar agora. Tenta de novo?'));
      }
    } finally {
      setBusy(false);
    }
  }

  function adjust() {
    markDraftHandled(activityId);
    navigate(`/servicos?servico=${encodeURIComponent(confirm.service)}`);
  }

  function dismiss() {
    markDraftHandled(activityId);
    onDismiss();
  }

  if (ready) {
    return (
      <section className="am-draft am-confirm" aria-label="Documento pronto">
        <header className="am-draft-to">
          <span className="am-draft-av" aria-hidden="true">
            ✓
          </span>
          <span>
            <small>Pronto</small>
            <b>{ready.title}</b>
          </span>
        </header>
        <div className="am-draft-acts">
          <button
            className="am-primary"
            onClick={() => {
              markDraftHandled(activityId);
              navigate(`/entregas/${encodeURIComponent(ready.id)}`);
            }}
          >
            Abrir o documento
          </button>
          <button className="am-ghost" onClick={dismiss}>
            Fechar
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="am-draft am-confirm" aria-label={`Preparar ${confirm.title}`}>
      <header className="am-draft-to">
        <span className="am-draft-av" aria-hidden="true">
          {confirm.title.trim().charAt(0).toUpperCase() || '•'}
        </span>
        <span>
          <small>Documento</small>
          <b>{confirm.title}</b>
        </span>
        <em>
          {confirm.remaining === null
            ? 'para conferir'
            : confirm.remaining === 1
              ? 'cabe mais 1 este mês'
              : `cabem mais ${confirm.remaining} este mês`}
        </em>
      </header>
      {confirm.rows.length > 0 && (
        <dl className="am-confirm-rows">
          {confirm.rows.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {error && <p className="am-note">{error}</p>}
      {busy && slow && !error && (
        <p className="am-note">
          Estou preparando. Pode levar meio minuto; eu aviso quando ficar pronto.
        </p>
      )}
      <div className="am-draft-acts">
        <button className="am-primary" onClick={() => void prepare()} disabled={busy}>
          {busy ? 'Preparando…' : 'Preparar'}
        </button>
        <button className="am-soft" onClick={adjust} disabled={busy}>
          Ajustar
        </button>
        <button className="am-ghost" onClick={dismiss} disabled={busy}>
          Agora não
        </button>
      </div>
    </section>
  );
}
